// CO-371 — this instance sends Accept-Language from i18n.language on EVERY request.
// The SPA previously never told the server its language, so every server refusal — including the
// Identity password rules CO-359 was written to surface — rendered in English on the ar/fr pages.
// The server localizes correctly; nothing was ever asking it to. Measured control: the same
// SignupFree payload returns English with no culture, Arabic with ?culture=ar, French with ?culture=fr.
// 🔴 Server side reads this via a CUSTOM provider that sets ONLY uiCulture and pins the FORMATTING
// culture to "en". Do NOT "simplify" that to the stock AcceptLanguageHeaderRequestCultureProvider:
// it also sets the formatting culture, and an ar/fr decimal comma can silently misread or PERSIST a
// wrong money value ([FromForm] binding parses with CurrentCulture; amounts round-trip through
// ToString()/float.TryParse).
import axios from "axios";
import errorHandling from './requestHandling';
import i18n from '../i18n';

// Falls back to the page's own origin when SERVER_URL isn't baked in at build
// time (deployed-dev ships it empty so one build serves both dev hosts on the
// shared dev DB). Local split-port dev and prod keep explicit env values.
const apiUrl = process.env.SERVER_URL || window.location.origin;

// A hang must eventually become a rejection.
//
// This instance had NO timeout, so a request that never answered never settled:
// the boot `Account/GetUserInfo` await in PrivateRoute could sit forever, the
// catch never ran, and the app was left on a shell (or a skeleton) with no
// error, no console output and no retry. 180s is deliberately far above every
// measured cold number on this stack (77s splash, 43s login, 31s GetUserInfo)
// so it NEVER fires on a healthy-but-slow request — its only job is to convert
// an infinite hang into a rejection the UI can surface and offer a retry for.
// Raise it, don't lower it, if a cold path is ever measured slower.
const service = axios.create({
    baseURL: apiUrl + "/api",
    timeout: 180000,
});

// Safely obtain the single Redux store instance that the app bootstraps in main.tsx
// main.tsx assigns (globalThis as any).__APP_STORE__ = store
function getAppStore(): any | undefined {
    try {
        const g: any = (globalThis as any);
        return g && g.__APP_STORE__ ? g.__APP_STORE__ : undefined;
    } catch {
        return undefined;
    }
}

// Centralized, robust token resolver. Never throws; returns string | null.
export function getAccessToken(): string | null {
    // 1) Try Redux store first (single instance exposed on globalThis)
    try {
        const store = getAppStore();
        const token = store?.getState?.()?.user?.userAuthInfo?.access_token;
        if (typeof token === 'string' && token.length > 0) return token;
    } catch {
        // ignore
    }

    // 2) Try OIDC storage entries (created by oidc-client/oidc libraries)
    try {
        const keys = Object.keys(localStorage).filter(key =>
            key.startsWith('oidc.user:') ||
            (key.startsWith('oidc.') && (localStorage.getItem(key) ?? '').includes('access_token'))
        );
        for (const key of keys) {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            try {
                const parsed = JSON.parse(raw);
                const token = parsed?.access_token;
                if (typeof token === 'string' && token.length > 0) return token;
            } catch {
                // skip invalid JSON
            }
        }
    } catch {
        // ignore
    }

    // 3) Try persisted Redux (redux-persist) root slice
    try {
        const persisted = localStorage.getItem('persist:root');
        if (persisted) {
            const root = JSON.parse(persisted);
            if (root && typeof root.user === 'string') {
                try {
                    const userState = JSON.parse(root.user);
                    const token = userState?.userAuthInfo?.access_token;
                    if (typeof token === 'string' && token.length > 0) return token;
                } catch {
                    // ignore parse errors
                }
            }
        }
    } catch {
        // ignore
    }

    return null;
}

// The server only supports en/ar/fr. i18next can hand back a region-tagged value
// ("ar-SA") or, before init resolves, `undefined` — normalize to a bare primary subtag
// and fall back to 'en' so we never send a header the server has no opinion about.
const SUPPORTED_LANGUAGES = ['en', 'ar', 'fr'];

function normalizeLanguage(language?: string): string {
    const primary = (language ?? '').split('-')[0].toLowerCase();
    return SUPPORTED_LANGUAGES.includes(primary) ? primary : 'en';
}

service.interceptors.response.use(
    (response) => {
        if (response.data instanceof Blob) {
            return response;
        }
        return response;
    },
    async (error) => errorHandling(error)
);

service.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers["timezoneOffset"] = new Date().getTimezoneOffset();
        // Tell the server which language this user is reading the app in, so that
        // SERVER-authored text (Identity password rules, ModelState validation, any
        // localized business refusal) comes back in that language instead of English.
        // Without this every server refusal rendered in English on the Arabic and French
        // pages — the server localizes correctly, the client simply never asked it to.
        //
        // `Accept-Language` is a CORS-safelisted request header, so it adds no preflight,
        // and Program.cs resolves it to the UI culture only (formatting culture stays "en").
        // An explicit `?culture=`/culture cookie still wins server-side, so this never
        // overrides a deliberate choice.
        config.headers["Accept-Language"] = normalizeLanguage(i18n.language);
        return config;
    },
    (error) => Promise.reject(error)
);

export default service;